import { forwardRef, useMemo, useRef, useState } from 'react'
import { CommonComboboxProps, ComboboxBase } from '../ComboboxBase'
import { SingleValue } from '../../Select/types'
import { useFloatingDropdown } from '../useFloatingDropdown'
import { useCombobox } from '../useCombobox'
import clsx from 'clsx'
import styles from '../Combobox.module.scss'
import { Chip } from '../Chip'

export interface ComboboxSingleProps extends CommonComboboxProps {
  defaultValue?: SingleValue
  value?: SingleValue
  onChange?: (value: SingleValue) => void
}

export const ComboboxSingle = forwardRef<HTMLDivElement, ComboboxSingleProps>(
  (
    {
      placeholder,
      children,
      defaultValue,
      defaultIsOpen = false,
      value,
      onChange,
      onBlur,
      onFocus,
      disabled = false,
      searchPlaceholder,
      size = 'md',
      dropdownMaxHeight,
      showSearch = true,
      ...props
    },
    ref,
  ) => {
    const {
      refs,
      floatingStyles,
      context,
      isOpen,
      setIsOpen,
      elementsRef,
      labelsRef,
      getReferenceProps,
      getFloatingProps,
      getItemProps,
      activeIndex,
    } = useFloatingDropdown({
      defaultIsOpen,
      disabled,
      dropdownMaxHeight,
    })

    const { selectedIndexes, handleSelect, selectedChildren } = useCombobox({
      defaultValue,
      value,
      onChange: (val) => {
        onChange?.(val)
        setIsOpen(false)
        selectContext.setSearchTerm('')
      },
      children,
      allowMultiple: false,
    })

    const [searchTerm, setSearchTerm] = useState('')
    const filteredElementsRef = useRef(elementsRef.current)

    const selectContext = useMemo(
      () => ({
        activeIndex,
        selectedIndexes,
        getItemProps,
        handleSelect,
        size,
        searchTerm,
        setSearchTerm,
        showSearch,
        allowMultiple: false,
      }),
      [activeIndex, selectedIndexes, getItemProps, handleSelect, size, searchTerm, showSearch],
    )
    return (
      <ComboboxBase
        ref={ref}
        disabled={disabled}
        size={size}
        isOpen={isOpen}
        refs={refs}
        floatingStyles={floatingStyles}
        context={context}
        children={children}
        showSearch={showSearch}
        searchPlaceholder={searchPlaceholder}
        selectedChildren={
          <>
            {selectedChildren?.length === 0 ? (
              <div className={clsx('mui-select-multi-placeholder', styles.placeholder)}>
                {placeholder}
              </div>
            ) : null}

            <div className={clsx('mui-select-multi-chips-wrapper', styles.container, styles[size])}>
              {selectedChildren.map((child, index) => (
                <Chip
                  size={size}
                  key={index}
                  icon={child.icon}
                  className={styles.unStyled}
                  onRemove={() => handleSelect(child.index)}
                >
                  {child?.children}
                </Chip>
              ))}
            </div>
          </>
        }
        referenceProps={getReferenceProps({ onBlur, onFocus })}
        floatingProps={getFloatingProps()}
        elementsRef={filteredElementsRef}
        labelsRef={labelsRef}
        selectContext={selectContext}
        {...props}
      />
    )
  },
)

ComboboxSingle.displayName = 'ComboboxSingle'
